import { useState } from 'react';
import {
    Dropdown,
    Button,
    Input,
    Select,
    Space,
    InputNumber,
    DatePicker,
    Radio,
} from 'antd';
import {
    FilterOutlined,
    PlusOutlined,
    DeleteOutlined,
    DownOutlined,
} from '@ant-design/icons';
// Example: define your columns and their types
import { columnDefs } from '../AssetManagerContext';
import { enumOptions } from '../AssetManagerContext';
import { useFilter } from '../AssetManagerContext';

const operatorOptions = {
    number: ['=', '>', '<', '>=', '<=', 'between'],
    string: ['equals', 'contains', 'starts with', 'ends with'],
    enum: ['contains'],
    'multi enum': ['contains'],
    boolean: ['is'], // boolean operators
    date: ['=', 'before', 'after', 'between'], // date operators
};

export function Filter() {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState([]);
    const { setAssetFilters, filterLogic, setFilterLogic } = useFilter();
    const onApplyFilters = (filters) => setAssetFilters(filters);

    const addFilter = () => {
        setFilters([
            ...filters,
            { field: null, operator: null, value: null, type: null },
        ]);
    };

    const removeFilter = (index) => {
        setFilters(filters.filter((_, i) => i !== index));
    };

    const updateFilter = (index, key, value) => {
        const next = [...filters];
        next[index][key] = value;
        // reset dependent fields when needed
        if (key === 'field') {
            next[index].operator = null;
            next[index].value = null;
            next[index].type = null;
        }
        setFilters(next);
    };

    const handleApply = () => {
        onApplyFilters(filters.filter((f) => f.field && f.operator));
        setOpen(false);
    };

    const handleClear = () => {
        setFilters([]);
        onApplyFilters([]);
    };

    const overlay = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                background: 'white',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                minWidth: 400,
                width: 'auto',
                padding: 8,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* All / Any selector */}
            <div style={{ marginBottom: 12 }}>
                <Radio.Group
                    value={filterLogic}
                    onChange={(e) => setFilterLogic(e.target.value)}
                    size="small"
                >
                    <Radio.Button value="all">All satisfy (AND)</Radio.Button>
                    <Radio.Button value="any">Any satisfy (OR)</Radio.Button>
                </Radio.Group>
            </div>

            <div
                style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    marginBottom: 8,
                }}
            >
                {filters.map((filter, index) => {
                    const type = columnDefs[filter.field];
                    const ops = operatorOptions[type] || [];

                    return (
                        <Space
                            key={index}
                            style={{
                                display: 'flex',
                                marginBottom: 8,
                                width: '100%',
                                justifyContent: 'space-between',
                            }}
                            align="center"
                        >
                            {/* Field */}
                            <Select
                                placeholder="Field"
                                value={filter.field}
                                onChange={(val) => {
                                    updateFilter(index, 'field', val);
                                }}
                                style={{ width: 100 }}
                                popupMatchSelectWidth={false}
                                showSearch
                                allowClear
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                options={Object.keys(columnDefs || {})
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((c) => ({
                                        label: c,
                                        value: c,
                                    }))}
                            />

                            {/* Operator */}
                            <Select
                                placeholder="Op"
                                value={filter.operator}
                                onChange={(val) =>
                                    updateFilter(index, 'operator', val)
                                }
                                style={{ width: 84 }}
                                popupMatchSelectWidth={false}
                                options={ops.map((op) => ({
                                    label: op,
                                    value: op,
                                }))}
                                disabled={!filter.field}
                            />

                            {!type && (
                                <Input
                                    placeholder="Value"
                                    value={filter.value}
                                    onChange={(e) =>
                                        updateFilter(
                                            index,
                                            'value',
                                            e.target.value
                                        )
                                    }
                                    style={{ width: 160 }}
                                    disabled={!filter.operator}
                                />
                            )}

                            {/* Value input */}
                            {type === 'number' &&
                                (filter.operator === 'between' ? (
                                    <Space>
                                        <InputNumber
                                            placeholder="min"
                                            value={filter.value?.[0]}
                                            onChange={(v) => {
                                                updateFilter(index, 'value', [
                                                    v,
                                                    filter.value?.[1],
                                                ]);
                                                updateFilter(
                                                    index,
                                                    'type',
                                                    'number'
                                                );
                                            }}
                                            style={{ width: 80 }}
                                        />
                                        <InputNumber
                                            placeholder="max"
                                            value={filter.value?.[1]}
                                            onChange={(v) => {
                                                updateFilter(index, 'value', [
                                                    filter.value?.[0],
                                                    v,
                                                ]);
                                                updateFilter(
                                                    index,
                                                    'type',
                                                    'number'
                                                );
                                            }}
                                            style={{ width: 80 }}
                                        />
                                    </Space>
                                ) : (
                                    <InputNumber
                                        placeholder="Value"
                                        value={filter.value}
                                        onChange={(v) => {
                                            updateFilter(index, 'value', v);
                                            updateFilter(
                                                index,
                                                'type',
                                                'number'
                                            );
                                        }}
                                        style={{ width: 160 }}
                                        disabled={!filter.operator}
                                    />
                                ))}

                            {type === 'string' && (
                                <Input
                                    placeholder="Value"
                                    value={filter.value}
                                    onChange={(e) => {
                                        updateFilter(
                                            index,
                                            'value',
                                            e.target.value
                                        );
                                        updateFilter(index, 'type', 'string');
                                    }}
                                    style={{ width: 160 }}
                                    disabled={!filter.operator}
                                />
                            )}

                            {type === 'enum' && (
                                <Select
                                    showSearch
                                    placeholder={`Select ${
                                        filter.field || 'value'
                                    }`}
                                    value={filter.value}
                                    onChange={(val) => {
                                        updateFilter(index, 'value', val);
                                        updateFilter(index, 'type', 'enum');
                                    }}
                                    popupMatchSelectWidth={false}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    style={{ width: 160 }}
                                    options={enumOptions[filter.field]?.map(
                                        (status) => ({
                                            value: status.toLowerCase(),
                                            label: status,
                                        })
                                    )}
                                    allowClear
                                    disabled={!filter.operator}
                                />
                            )}

                            {type === 'multi enum' && (
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder={`Select ${
                                        filter.field || 'values'
                                    }`}
                                    value={filter.value || []} // must be array
                                    onChange={(vals) => {
                                        updateFilter(index, 'value', vals);
                                        updateFilter(
                                            index,
                                            'type',
                                            'multi enum'
                                        );
                                    }}
                                    popupMatchSelectWidth={false}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    style={{ width: 160 }}
                                    options={enumOptions[filter.field]?.map(
                                        (status) => ({
                                            value: status.toLowerCase(),
                                            label: status,
                                        })
                                    )}
                                    allowClear
                                    disabled={!filter.operator}
                                />
                            )}

                            {type === 'date' &&
                                (filter.operator === 'between' ? (
                                    <DatePicker.RangePicker
                                        value={filter.value || []}
                                        onChange={(dates) => {
                                            updateFilter(index, 'value', dates);
                                            updateFilter(index, 'type', 'date');
                                        }}
                                        style={{ width: 185 }}
                                        disabled={!filter.operator}
                                    />
                                ) : (
                                    <DatePicker
                                        value={filter.value || null}
                                        onChange={(date) => {
                                            updateFilter(index, 'value', date);
                                            updateFilter(index, 'type', 'date');
                                        }}
                                        style={{ width: 160 }}
                                        disabled={!filter.operator}
                                    />
                                ))}

                            {type === 'boolean' && (
                                <Select
                                    placeholder="Select value"
                                    value={filter.value}
                                    onChange={(val) => {
                                        updateFilter(index, 'value', val);
                                        updateFilter(index, 'type', 'boolean');
                                    }}
                                    style={{ width: 160 }}
                                    options={[
                                        { label: 'True', value: true },
                                        { label: 'False', value: false },
                                    ]}
                                    disabled={!filter.operator}
                                    allowClear
                                />
                            )}

                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeFilter(index)}
                            />
                        </Space>
                    );
                })}
            </div>

            {/* Add button */}
            <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addFilter}
                style={{ marginBottom: 8 }}
                block
            >
                Add Filter
            </Button>

            {/* Footer */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 8,
                }}
            >
                <Button size="small" onClick={handleClear}>
                    Clear
                </Button>
                <Button type="primary" size="small" onClick={handleApply}>
                    Apply
                </Button>
            </div>
        </div>
    );

    return (
        <Dropdown
            dropdownRender={() => overlay}
            open={open}
            onOpenChange={setOpen}
            trigger={['click']}
            placement="bottomLeft"
        >
            <Button icon={<FilterOutlined />}>
                Filter <DownOutlined />
            </Button>
        </Dropdown>
    );
}
